import angular from 'angular';
import sanitize from 'angular-sanitize';
import bootstrap from 'angular-ui-bootstrap';
import i18n from '../i18n';

import popoverTemplate, { SHOW_EVENT, HIDE_EVENT, POPUP_DELAY } from './popover-template-factory';

/**
 * @ngdoc module
 * @name akamai.components.popover
 *
 * @guideline https://design.akamai.com/components/popover/
 *
 * @description
 * The popover control allows for extensible popover creation. It supports multiple placements,
 * triggers, optional transition animation and more.
 *
 * @example index.html
 * {% raw %}
 * <span akam-popover="Text Content Only." popover-placement="top"
 *   popover-trigger="click">Click Here - Top
 * </span>
 *
 * <span akam-popover="Text Content Only." popover-placement="left"
 *   popover-trigger="mouseenter">Hover Here - Left
 * </span>
 *
 * <span akam-popover="{{safeHtml}}" popover-placement="top"
 *   popover-trigger="click">Using trusted html
 * </span>
 *
 * <span akam-popover-template="custom-template"
 *   popover-placement="right"
 *   popover-title="Header Title"
 *   popover-trigger="mouseenter">Hover Here - Right
 * </span>
 * {% endraw %}
 * @example index.js
 *
 * $scope.safeHtml = '<h2>HTML Binding</h2><br/><br/><p>Used in <strong>popover</strong>.</p>';
 * $scope.customData = 'aTemplateId.html';
 *
 * function configFn($templateCache) {
 *   $templateCache.put('aTemplateId.html', '...');
 * }
 *
 */
export default angular.module('akamai.components.popover', [
  sanitize,
  bootstrap,
  i18n.name
])
  .factory('akamPopoverTemplate', popoverTemplate)
  .config(configFn)

/**
 * @ngdoc directive
 *
 * @name akamPopover
 *
 * @description Creates a tool tip for an element
 *
 * @restrict A
 *
 * @param {string} akam-popover Popover text content. Also takes an expression that evaluates to an
 * html string.
 *
 * @param {string} [popover-placement=top] Where the popover appears [top, bottom, left, right].
 *
 * @param {string} [padding-size] Adds class that adds padding to the popover if 'large'.
 *
 * @param {string} [popover-title] The popover header.
 *
 * @param {string} [popover-trigger] What triggers the popover ['click', 'mouseenter'].
 *
 * @param {boolean} [popover-animation=true] Determines if the popover fades in and out.
 *
 * @param {number} [popover-popup-delay=200] milliseconds before the popover is shown.
 *
 * @param {boolean} [popover-append-to-body=false] If true, the popover will be appended to the
 * body. Use this when using a popover in a container that is set to `overflow: hidden`.
 *
 */
.directive('akamPopover', ['akamPopoverTemplate',
  akamPopoverTemplate => akamPopoverTemplate('popover')
])

/**
 * @ngdoc directive
 *
 * @name akamPopoverTemplate
 *
 * @description Creates a tool tip for an element using a custom html template
 *
 * @restrict A
 *
 * @param {string} akam-popover-template Popover template location
 *
 * @param {string} [popover-placement=top] Where popover appears [top, top-left, top-right, bottom,
 * bottom-left, bottom-right, left, left-top, left-bottom, right, right-top, right-bottom, auto].
 * Passing in 'auto' separated by a space before the placement will enable auto positioning,
 * e.g: "auto bottom-left". The popover will attempt to position where it fits in the closest
 * scrollable ancestor
 *
 * @param {string} [popover-title] Popover header
 *
 * @param {string} [popover-trigger] Popover trigger ['click', 'mouseenter']
 *
 * @param {boolean} [popover-animation=true] Determines if the popover fades in and out.
 *
 * @param {number} [popover-popup-delay=200] milliseconds before the popover is shown.
 *
 * @param {boolean} [popover-append-to-body=false] If true, the popover will be appended to the
 * body. Use this when using a popover in a container that is set to `overflow: hidden`.
 *
 */
  .directive('akamPopoverTemplate', ['akamPopoverTemplate',
    akamPopoverTemplate => akamPopoverTemplate('popover-template')
  ]);

function configFn($uibTooltipProvider) {
  $uibTooltipProvider.options({
    popupDelay: POPUP_DELAY
  });

  $uibTooltipProvider.setTriggers({
    [SHOW_EVENT]: HIDE_EVENT
  });
}
configFn.$inject = ['$uibTooltipProvider'];